import { useCallback, useRef } from 'react'
import { router } from '@inertiajs/react'
import { toast } from 'sonner'
import { useSnoozePicker } from '@/hooks/useSnoozePicker'
import { fetchJson } from '@/components/inbox/utils'
import type { ConversationListItem, Agent } from '@/components/inbox/types'

interface UseConversationActionsParams {
  selectedConversationId: number | null
  filteredConversations: ConversationListItem[]
  buildFilterUrl: (conversationId?: number | null) => string
  conversations: ConversationListItem[]
  setConversations: React.Dispatch<React.SetStateAction<ConversationListItem[]>>
  setSelectedConversation: React.Dispatch<React.SetStateAction<import('@/components/inbox/types').ConversationDetail | null>>
  setStats: React.Dispatch<React.SetStateAction<{ all: number; assigned_to_me: number; unassigned: number; open: number; closed: number; snoozed: number }>>
  agents: Agent[]
  currentAgentId: number
}

export function useConversationActions({
  selectedConversationId,
  filteredConversations,
  buildFilterUrl,
  conversations,
  setConversations,
  setSelectedConversation,
  setStats,
  agents,
  currentAgentId,
}: UseConversationActionsParams) {
  const snoozeButtonRef = useRef<HTMLButtonElement>(null)

  const navigateToNextConversation = useCallback(() => {
    const currentIndex = filteredConversations.findIndex((c) => c.id === selectedConversationId)
    const nextConversation = filteredConversations[currentIndex + 1] || filteredConversations[currentIndex - 1]
    router.visit(buildFilterUrl(nextConversation?.id || null), { preserveState: false })
  }, [filteredConversations, selectedConversationId, buildFilterUrl])

  const handleSnoozeConversation = useCallback(async (datetime: Date) => {
    if (!selectedConversationId) return
    const response = await fetchJson(`/conversations/${selectedConversationId}`, {
      method: 'PATCH',
      body: { conversation: { status: 'snoozed', snoozed_until: datetime.toISOString() } },
    })
    if (!response.ok) throw new Error('Failed to snooze conversation')
  }, [selectedConversationId])

  const snoozePicker = useSnoozePicker({
    onSnooze: handleSnoozeConversation,
    onComplete: navigateToNextConversation,
  })

  const handleCloseConversation = useCallback(async () => {
    if (!selectedConversationId) return
    const response = await fetchJson(`/conversations/${selectedConversationId}`, {
      method: 'PATCH',
      body: { conversation: { status: 'closed' } },
    })
    if (response.ok) {
      toast.success('Conversa fechada')
      navigateToNextConversation()
    } else {
      toast.error('Erro ao fechar a conversa')
    }
  }, [selectedConversationId, navigateToNextConversation])

  const handleCloseWithRating = useCallback(async () => {
    if (!selectedConversationId) return
    try {
      await fetchJson(`/conversations/${selectedConversationId}/request_rating`, { method: 'POST' })
    } catch {}
    const response = await fetchJson(`/conversations/${selectedConversationId}`, {
      method: 'PATCH',
      body: { conversation: { status: 'closed' } },
    })
    if (response.ok) {
      toast.success('Conversa fechada')
      navigateToNextConversation()
    } else {
      toast.error('Erro ao fechar a conversa')
    }
  }, [selectedConversationId, navigateToNextConversation])

  const handleReopenConversation = useCallback(async () => {
    if (!selectedConversationId) return
    const response = await fetchJson(`/conversations/${selectedConversationId}`, {
      method: 'PATCH',
      body: { conversation: { status: 'open' } },
    })
    if (response.ok) { toast.success('Conversa reaberta'); router.reload() }
    else toast.error('Erro ao reabrir a conversa')
  }, [selectedConversationId])

  const handleUnsnoozeConversation = useCallback(async () => {
    if (!selectedConversationId) return
    const response = await fetchJson(`/conversations/${selectedConversationId}`, {
      method: 'PATCH',
      body: { conversation: { status: 'open', snoozed_until: null } },
    })
    if (response.ok) { toast.success('Conversa retomada'); router.reload() }
    else toast.error('Erro ao retomar a conversa')
  }, [selectedConversationId])

  // Agent assignment
  const handleAssignAgent = useCallback(async (agentId: number) => {
    if (!selectedConversationId) return
    const agent = agents.find((a) => a.id === agentId) || null
    const conv = conversations.find((c) => c.id === selectedConversationId)
    const isOpen = conv?.status === 'open'

    if (isOpen && conv) {
      const wasUnassigned = !conv.assigned_agent
      const wasAssignedToMe = conv.assigned_agent?.id === currentAgentId
      const assigningToMe = agentId === currentAgentId
      setStats((prev) => ({
        ...prev,
        unassigned: prev.unassigned - (wasUnassigned ? 1 : 0),
        assigned_to_me: prev.assigned_to_me + (assigningToMe && !wasAssignedToMe ? 1 : 0) - (wasAssignedToMe && !assigningToMe ? 1 : 0),
      }))
    }

    setSelectedConversation((prev) => (prev ? { ...prev, assigned_agent: agent } : prev))
    setConversations((prev) => prev.map((c) => (c.id === selectedConversationId ? { ...c, assigned_agent: agent } : c)))

    const response = await fetchJson(`/conversations/${selectedConversationId}`, {
      method: 'PATCH',
      body: { conversation: { assigned_agent_id: agentId } },
    })
    if (!response.ok) { toast.error('Erro ao atribuir agente'); router.reload() }
  }, [selectedConversationId, agents, conversations, currentAgentId])

  const handleUnassignAgent = useCallback(async () => {
    if (!selectedConversationId) return
    const conv = conversations.find((c) => c.id === selectedConversationId)
    const isOpen = conv?.status === 'open'

    if (isOpen && conv) {
      const wasAssignedToMe = conv.assigned_agent?.id === currentAgentId
      const hadAgent = !!conv.assigned_agent
      setStats((prev) => ({
        ...prev,
        unassigned: prev.unassigned + (hadAgent ? 1 : 0),
        assigned_to_me: prev.assigned_to_me - (wasAssignedToMe ? 1 : 0),
      }))
    }

    setSelectedConversation((prev) => (prev ? { ...prev, assigned_agent: null } : prev))
    setConversations((prev) => prev.map((c) => (c.id === selectedConversationId ? { ...c, assigned_agent: null } : c)))

    const response = await fetchJson(`/conversations/${selectedConversationId}`, {
      method: 'PATCH',
      body: { conversation: { assigned_agent_id: null } },
    })
    if (!response.ok) { toast.error('Erro ao remover atribuição'); router.reload() }
  }, [selectedConversationId, conversations, currentAgentId])

  return {
    snoozeButtonRef,
    snoozePicker,
    navigateToNextConversation,
    handleCloseConversation,
    handleCloseWithRating,
    handleReopenConversation,
    handleUnsnoozeConversation,
    handleAssignAgent,
    handleUnassignAgent,
  }
}
